import type { FC, ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useWorkspaces } from 'hooks/Workspace';
import { EXPERT_ROUTES } from 'applications/expert/routing/expertRoutes';
import CustomLoader from 'components/molecules/CustomLoader.component';
import useWorkspaceDetails from 'hooks/useWorkspaceDetails';

interface WorkspaceGuardProps {
  children: ReactNode;
}

// Routes that don't require a selected workspace
const WORKSPACE_EXEMPT_ROUTES = [
  EXPERT_ROUTES.WORKSPACE,
  EXPERT_ROUTES.WORKSPACE_CREATE,
  EXPERT_ROUTES.CLIENT_INVITES_NOTIFICATION,
  EXPERT_ROUTES.LOGIN,
  EXPERT_ROUTES.SIGNUP,
  EXPERT_ROUTES.EXPERT_LOGIN,
  EXPERT_ROUTES.RESET_PASSWORD,
  EXPERT_ROUTES.SET_PASSWORD,
  EXPERT_ROUTES.ACCOUNT_SETTINGS,
  EXPERT_ROUTES.NOT_FOUND,
];

/**
 * WorkspaceGuard ensures that a valid workspace is selected before
 * allowing access to pages that require workspace context.
 *
 * If no valid workspace is selected, redirects to workspace selection page.
 */
const WorkspaceGuard: FC<WorkspaceGuardProps> = ({ children }) => {
  const location = useLocation();
  const { pathname } = location;
  const { selectedWorkspace, isLoading } = useWorkspaces();
  const { workspaceId: storedWorkspaceId } = useWorkspaceDetails();

  // Check if current route is exempt from workspace requirement
  const isExemptRoute = WORKSPACE_EXEMPT_ROUTES.some((route) =>
    pathname.startsWith(route),
  );

  // Show loading state while fetching workspaces
  if (isLoading) {
    return (
      <div className="flex flex-1 h-screen items-center justify-center bg-white">
        <CustomLoader color="primary" />
      </div>
    );
  }

  // If route doesn't need workspace, render normally
  if (isExemptRoute) {
    return <>{children}</>;
  }

  // If no valid workspace is selected, or selected does not match stored, redirect
  if (
    !selectedWorkspace ||
    !storedWorkspaceId ||
    selectedWorkspace._id !== storedWorkspaceId
  ) {
    // Add autoSelect param only when coming from initial login (no workspace ID stored)
    const redirectPath = !storedWorkspaceId
      ? `${EXPERT_ROUTES.WORKSPACE}?autoSelect=true`
      : EXPERT_ROUTES.WORKSPACE;

    return <Navigate to={redirectPath} state={{ from: location }} replace />;
  }

  // Valid workspace selected, render children
  return <>{children}</>;
};

export default WorkspaceGuard;
